import './App.css';
import {Box, createTheme, CssBaseline, ThemeProvider, Typography} from '@mui/material';
import Login from './login/components/OperatorLogin';
import {useEffect, useMemo, useState} from 'react';
import {cookieExists} from './common/utils/CookieUtils';
import {LoadingProvider} from './loading-overlay/LoadingContext';
import Header from './common/components/Header';
import {ToastContainer} from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import PlayerWorkflow from './player-workflow/components/PlayerWorkflow';
import * as locales from '@mui/material/locale';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import 'dayjs/locale/cs';
import 'dayjs/locale/da';
import 'dayjs/locale/de';
import 'dayjs/locale/el';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/hu';
import 'dayjs/locale/it';
import 'dayjs/locale/ja';
import 'dayjs/locale/nl';
import 'dayjs/locale/nb';
import 'dayjs/locale/pl';
import 'dayjs/locale/pt';
import 'dayjs/locale/ro';
import 'dayjs/locale/ru';
import 'dayjs/locale/sv';
import 'dayjs/locale/tr';
import 'dayjs/locale/zh-cn';

dayjs.extend(localizedFormat);
function Copyright() {
  return (
    <Typography variant="body2" color="text.secondary" align="center">
      {'Copyright © RedCube GmbH '}
      {new Date().getFullYear()}
    </Typography>
  );
}

let theme = createTheme({
  palette: {
    primary: {
      light: '#64b8fa',
      main: '#081425',
      dark: '#005b96',
    },
    secondary: {
      light: '#68fbe6',
      main: '#20c7b4',
      dark: '#009584',
    },
  },
  typography: {
    h2: {
      fontWeight: 500,
      fontSize: '2.5em',
      letterSpacing: 0.5,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
    },
  },
  mixins: {
    toolbar: {
      minHeight: '5vh',
    },
  },
});

theme = {
  ...theme,
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#081627',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'outlined',
        size: 'large',
      },
      styleOverrides: {
        root: {
          marginTop: theme.spacing(3),
          marginBottom: theme.spacing(2),
          textTransform: 'none',
        },
        contained: {
          'boxShadow': 'none',
          '&:active': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: theme.spacing(1),
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 4,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: '#4fc3f7',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: 14,
          fontWeight: theme.typography.fontWeightMedium,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          'color': 'inherit',
          'minWidth': 'auto',
          'marginRight': theme.spacing(2),
          '& svg': {
            fontSize: 20,
          },
        },
      },
    },
  },
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [themeLocale, setThemeLocale] = useState('enUs');
  const [dateLocale, setDateLocale] = useState('en');

  const setLanguage = (newLanguage) => {
    setThemeLocale(newLanguage.muiLocale);
    setDateLocale(newLanguage.dateLocale);
  };
  const themeWithLocale = useMemo(
    () => {
      const locale = locales[themeLocale];
      return createTheme(theme, locale);
    },
    [themeLocale, theme],
  );
  const checkIfLoggedIn = () => {
    setIsLoggedIn(cookieExists('loggedInIndicator'));
  };

  useEffect(() => {
    checkIfLoggedIn();
  }, []);

  let mainContent;
  if (isLoggedIn) {
    mainContent = <PlayerWorkflow setLanguage={setLanguage}/>;
  } else {
    mainContent = <Login onLoginAttempt={checkIfLoggedIn}/>;
  }

  return (
    <ThemeProvider theme={themeWithLocale}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dateLocale}>
        <LoadingProvider>
          <Box sx={{display: 'flex', minHeight: '100vh'}}>
            <CssBaseline/>
            <Box sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
              <Header onAuthChange={checkIfLoggedIn}/>
              <Box component="main" sx={{flex: 1, py: 6, px: 4, bgcolor: '#eaeff1'}}>
                {mainContent}
              </Box>
              <Box component="footer" sx={{p: 2, bgcolor: '#eaeff1'}}>
                <Copyright/>
              </Box>
            </Box>
          </Box>
          <ToastContainer/>
        </LoadingProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
